// pages/master/role/MasterRoleAddEdit.jsx
'use client';

import { useState, useEffect } from 'react';
import { Box, Stack, TextField, CircularProgress, Button, Typography, InputAdornment } from '@mui/material';
import { LightbulbOutlined as LightbulbIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import PickerField from '../../../components/PickerField';
import FormDialog from '../../../components/FormDialog';
import { useNotifier } from '../../../components/Notifier';
import { useLanguage } from '../../../utils/i18n';

import { createRole, updateRole, getRoleById, getPackageMenuList, useRoleList } from '../../../stores/roleStore';

const MasterRoleAddEdit = ({ open, id = null, onClose, onSuccess }) => {
  const notify = useNotifier();
  const { t } = useLanguage();

  const { data: roleList } = useRoleList(0, 1);
  const fetchedPackage = roleList && roleList.length > 0 ? roleList[0].package_name : null;

  // 1. Ambil package_name dari useRoleList (data pertama) atau fallback ke local storage
  const currentUserPackage =
    fetchedPackage || (typeof window !== 'undefined' ? localStorage.getItem('package_name') || localStorage.getItem('package') || '' : '');

  const currentUserRole = typeof window !== 'undefined' ? localStorage.getItem('rolename') || localStorage.getItem('role_name') || '' : '';
  const isRoleAdmin = currentUserRole === 'Administrator';

  const [initialValues, setInitialValues] = useState({
    role_name: '',
    package_name: ''
  });

  const [loading, setLoading] = useState(false);
  const [packageOptions, setPackageOptions] = useState([]);

  // Fetch daftar Package
  useEffect(() => {
    if (!open) return;

    let mounted = true;
    const fetchPackages = async () => {
      try {
        const res = await getPackageMenuList(0, 100);
        if (!mounted) return;

        const arr = Array.isArray(res) ? res : (res?.data ?? []);

        // 3. IMPLEMENTASI KONDISI (CONDITIONING)
        if (isRoleAdmin) {
          // Jika Admin: Tampilkan semua opsi package dari API
          const options = arr.map((pkg) => ({
            value: pkg.package_name ?? String(pkg.id),
            label: pkg.package_name ?? String(pkg.id)
          }));
          setPackageOptions(options);
        } else {
          // Jika BUKAN Admin: Tampilkan hanya package miliknya sendiri
          setPackageOptions([{ value: currentUserPackage, label: currentUserPackage }]);
        }
      } catch (err) {
        console.error('Failed to fetch package menu list:', err);
        notify({ message: t('masterRole.failedLoadData', 'Failed to load Package list'), severity: 'error' });
      }
    };

    fetchPackages();

    return () => {
      mounted = false;
    };
  }, [open, notify, isRoleAdmin, currentUserPackage, t]);

  // Fetch detail Role saat Mode Edit
  useEffect(() => {
    if (!open) return;

    let mounted = true;
    const fetchRoleData = async () => {
      if (!id) {
        // Jika Mode TAMBAH (Add) dan bukan Admin, otomatis isi package_name
        if (!isRoleAdmin && currentUserPackage) {
          setInitialValues((prev) => ({ ...prev, package_name: currentUserPackage }));
        } else {
          setInitialValues({ role_name: '', package_name: '' });
        }
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const data = await getRoleById(id);
        if (!mounted) return;

        if (data) {
          setInitialValues((prev) => ({
            ...prev,
            role_name: data.role_name ?? prev.role_name,
            package_name: data.package_name ?? data.join?.package_name ?? prev.package_name
          }));
        }
      } catch (err) {
        console.error('Failed to load role:', err);
        notify({ message: t('masterRole.failedLoadData', 'Failed to load role data'), severity: 'error' });
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchRoleData();

    return () => {
      mounted = false;
    };
  }, [id, open, notify, isRoleAdmin, currentUserPackage, t]);

  const validationSchema = Yup.object().shape({
    role_name: Yup.string()
      .required(`${t('masterRole.roleName', 'Role Name')} ${t('general.required', 'is required')}`)
      .test('smart-validation', 'Format tidak valid', function (value) {
        if (!value) return true;

        const hasSpace = /\s/.test(value);
        const hasSpecialChar = /[^a-zA-Z0-9_\-\s]/.test(value);

        if (hasSpace && hasSpecialChar) {
          return this.createError({
            message: t(
              'masterRole.smartValidation1',
              'Tidak diperbolehkan ada spasi dan karakter unik. Hanya huruf, angka, strip (-), dan underscore (_)'
            )
          });
        } else if (hasSpace) {
          return this.createError({
            message: t('masterRole.smartValidation2', 'Tidak diperbolehkan ada spasi. Hanya huruf, angka, strip (-), dan underscore (_)')
          });
        } else if (hasSpecialChar) {
          return this.createError({
            message: t(
              'masterRole.smartValidation3',
              'Tidak diperbolehkan ada karakter unik. Hanya huruf, angka, strip (-), dan underscore (_)'
            )
          });
        }

        return true;
      }),
    package_name: Yup.string().required(`${t('masterRole.packageName', 'Package Name')} ${t('general.required', 'is required')}`)
  });

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const payload = {
          role_name: values.role_name ?? '',
          package_name: values.package_name ?? ''
        };

        if (id) {
          await updateRole(id, payload);
          notify({ message: t('masterRole.updateRoleSuccess', 'Role successfully updated'), severity: 'success' });
        } else {
          let storedRole = null;
          try {
            storedRole = localStorage.getItem('rolename');
          } catch (e) {
            console.warn('Gagal membaca localStorage', e);
          }

          payload.role_parent = storedRole;

          await createRole(payload);
          notify({ message: t('masterRole.createRoleSuccess', 'Role successfully created'), severity: 'success' });
        }

        if (onSuccess) onSuccess();
        if (onClose) onClose();
      } catch (err) {
        console.error('Submit error:', err);
        const errorMessage =
          err?.response?.data?.message || err?.data?.message || err?.message || t('notify.failedSave', 'Failed to save data');
        notify({ message: errorMessage, severity: 'error' });
      } finally {
        setSubmitting(false);
      }
    }
  });

  const dialogActions = (
    <>
      <Button onClick={onClose} disabled={formik.isSubmitting} sx={{ color: 'text.secondary', textTransform: 'none' }}>
        {id && !isRoleAdmin ? t('general.close', 'Close') : t('general.cancel', 'Cancel')}
      </Button>
      {(!id || isRoleAdmin) && (
        <Button
          variant="contained"
          color="primary"
          onClick={formik.handleSubmit}
          disabled={formik.isSubmitting || loading}
          startIcon={formik.isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
          sx={{ textTransform: 'none', borderRadius: 1, px: 3, fontWeight: 700 }}
        >
          {formik.isSubmitting ? t('general.processing', 'Processing...') : t('general.save', 'Save')}
        </Button>
      )}
    </>
  );

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={
        id ? (isRoleAdmin ? t('masterRole.edit', 'Edit Role') : t('masterRole.detail', 'Detail Role')) : t('masterRole.add', 'Add Role')
      }
      maxWidth="sm"
      fullScreenMode={false}
      actions={dialogActions}
    >
      <Box sx={{ width: '100%', pt: 1 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <form onSubmit={formik.handleSubmit}>
            <Stack spacing={3}>
              {!id && (
                <Box
                  sx={{
                    bgcolor: 'primary.50',
                    p: 2,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.5,
                    border: '1px solid',
                    borderColor: 'primary.100'
                  }}
                >
                  <LightbulbIcon color="primary" sx={{ mt: 0.2 }} />
                  <Box>
                    <Typography variant="subtitle2" color="primary.900" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {t('masterRole.tipsRoleName', 'Role Name Tips')}
                    </Typography>
                    <Typography variant="body2" color="primary.800" sx={{ lineHeight: 1.5 }}>
                      {t('masterRole.tipsRoleNameDesc1', 'Make sure the role name is written')}{' '}
                      <strong>{t('masterRole.tipsRoleNameDesc2', 'without spaces and special characters')}</strong>{' '}
                      {t(
                        'masterRole.tipsRoleNameDesc3',
                        'so the system can process it well. Only use letters, numbers, dash (-), and underscore (_).'
                      )}
                      <br />
                      {t('masterRole.tipsRoleNameDesc4', 'Correct examples: ')}{' '}
                      <span style={{ fontFamily: 'monospace', background: '#fff', padding: '2px 6px', borderRadius: '4px' }}>
                        adm_subhold
                      </span>
                      ,{' '}
                      <span style={{ fontFamily: 'monospace', background: '#fff', padding: '2px 6px', borderRadius: '4px' }}>
                        driver_lead
                      </span>
                    </Typography>
                  </Box>
                </Box>
              )}

              <TextField
                label={`${t('masterRole.roleName', 'Role Name')} *`}
                name="role_name"
                value={formik.values.role_name ?? ''}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                size="medium"
                fullWidth
                error={formik.touched.role_name && Boolean(formik.errors.role_name)}
                helperText={
                  formik.touched.role_name && formik.errors.role_name
                    ? formik.errors.role_name
                    : id
                      ? t('masterRole.integrityWarning', 'To maintain data integrity, role name cannot be changed after creation.')
                      : t(
                          'masterRole.smartValidation1',
                          'No spaces and special characters allowed. Only letters, numbers, dash (-), and underscore (_)'
                        )
                }
                disabled={!!id}
                InputProps={{
                  endAdornment:
                    formik.values.role_name && !formik.errors.role_name && !id ? (
                      <InputAdornment position="end">
                        <CheckCircleIcon color="success" />
                      </InputAdornment>
                    ) : null
                }}
                sx={{
                  '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: 'rgba(0, 0, 0, 0.87) !important',
                    color: 'rgba(0, 0, 0, 0.87) !important',
                    fontWeight: 600
                  },
                  '& .MuiFormHelperText-root': {
                    color: id ? 'rgba(0, 0, 0, 0.87)' : 'text.secondary',
                    fontWeight: id ? 500 : 400
                  }
                }}
              />

              <PickerField
                label={`${t('masterRole.packageName', 'Package')} *`}
                name="package_name"
                value={formik.values.package_name ?? ''}
                options={packageOptions}
                onChange={(val) => {
                  const v = typeof val === 'object' && val !== null ? val.value : val;
                  formik.setFieldValue('package_name', v);
                }}
                dialogTitle={t('general.select', 'Select') + ' ' + t('masterRole.packageName', 'Package')}
                error={formik.touched.package_name && !!formik.errors.package_name}
                helperText={
                  formik.touched.package_name && formik.errors.package_name
                    ? formik.errors.package_name
                    : !isRoleAdmin
                      ? t('masterRole.packageWarning', 'Package Name is automatically filled and cannot be changed.')
                      : ''
                }
                disabled={!isRoleAdmin} // 4. Kunci dropdown jika bukan Administrator
                sx={{
                  '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: 'rgba(0, 0, 0, 0.87) !important',
                    color: 'rgba(0, 0, 0, 0.87) !important',
                    fontWeight: 600
                  },
                  '& .MuiFormLabel-root.Mui-disabled': {
                    color: 'rgba(0, 0, 0, 0.87)'
                  },
                  '& .MuiFormHelperText-root': {
                    color: !isRoleAdmin ? 'rgba(0, 0, 0, 0.87)' : 'text.secondary',
                    fontWeight: !isRoleAdmin ? 500 : 400
                  }
                }}
              />
            </Stack>
          </form>
        )}
      </Box>
    </FormDialog>
  );
};

export default MasterRoleAddEdit;
